import { authTrpcContract } from "@amtana/auth-contract";
import type {
  AuthenticationOptions,
  AuthenticationResponseInput,
  AuthSession,
  ChangePasswordInput,
  ConfirmEmailChangeInput,
  ConfirmEmailVerificationInput,
  ConfirmPasswordResetInput,
  DeleteAccountInput,
  DeletionStarted,
  EmailChanged,
  EndSessionInput,
  OidcProviderInput,
  OidcRedirect,
  OidcSignedIn,
  OidcSignInInput,
  OidcSignInStartInput,
  Passkey,
  Passkeys,
  PasskeySignInInput,
  RegistrationOptions,
  RegistrationResponseInput,
  RenamePasskeyInput,
  RequestEmailChangeInput,
  SessionsList,
  SignedIn,
  SignInInput,
  SignInRoute,
  SignUpInput,
  Verified,
} from "@amtana/auth-contract";
import { useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { MutationResult, QueryResult } from "@amtana/browser";

// Who is signed in, for every browser module. The session itself lives on the
// server (ADR-004 §2): signing in, up or out sets or clears the cookie
// itself, HttpOnly, as part of the response. This package never reads or
// writes it; it only refreshes the cache once the server's answer lands.

export function useSession(): QueryResult<AuthSession> {
  return useContractQuery(authTrpcContract, "session", { input: {} });
}

/** Signing in or up starts a new session: every cached read belonged to someone else. */
function useEnter<TInput>(
  name: "signIn" | "signUp" | "confirmPasswordReset" | "passkeySignIn",
): MutationResult<SignedIn, TInput> {
  const mutation = useContractMutation(authTrpcContract, name);
  const invalidate = useInvalidateContract();
  // The contract types `name`'s input and output; the hook restates them for consumers.
  const typed = mutation as unknown as MutationResult<SignedIn, TInput>;
  return {
    ...typed,
    run: async (input: TInput) => {
      const signedIn = await typed.mutateAsync(input);
      await invalidate();
      return signedIn;
    },
  };
}

export const useSignIn = (): MutationResult<SignedIn, SignInInput> => useEnter("signIn");
export const useSignUp = (): MutationResult<SignedIn, SignUpInput> => useEnter("signUp");

/** Ends the caller's own session on the server; the response clears its cookie. */
/** The sign-in page, loaded fresh once a session ends: a full load drops every read the screens
 * still hold, so none of them re-asks the server for data it may no longer read (a refresh after
 * signing out answered every one with 401 and blanked the page on its way to /login). */
function openSignInFresh(): void {
  window.location.replace("/login");
}

export function useSignOut(): () => Promise<void> {
  const mutation = useContractMutation(authTrpcContract, "signOut");
  return async () => {
    await mutation.mutateAsync({});
    openSignInFresh();
  };
}

export function useRequestPasswordReset(): MutationResult<{ ok: true }, { email: string }> {
  return useContractMutation(authTrpcContract, "requestPasswordReset") as unknown as MutationResult<
    { ok: true },
    { email: string }
  >;
}

/** Spends the token a mailed reset link carried and sets the new password; starts a fresh session. */
export const useConfirmPasswordReset = (): MutationResult<SignedIn, ConfirmPasswordResetInput> =>
  useEnter("confirmPasswordReset");

/** Mails a fresh verification link to the signed-in caller's own address. */
export function useRequestEmailVerification(): MutationResult<{ ok: true }, Record<string, never>> {
  return useContractMutation(authTrpcContract, "requestEmailVerification") as unknown as MutationResult<
    { ok: true },
    Record<string, never>
  >;
}

/** Spends the token a mailed verification link carried. */
export function useConfirmEmailVerification(): MutationResult<Verified, ConfirmEmailVerificationInput> {
  const mutation = useContractMutation(authTrpcContract, "confirmEmailVerification");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<Verified, ConfirmEmailVerificationInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

/** Where an email signs in, asked as it is typed: a complete address only. */
export function useSignInRoute(email: string): QueryResult<SignInRoute> {
  const complete = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  return useContractQuery(authTrpcContract, "signInRoute", {
    input: { email: email.trim() },
    enabled: complete,
  });
}

/**
 * Starts the session of someone another module just signed in (single
 * sign-on): that call's own response already set the cookie, so this only
 * refreshes every cached read, since it belonged to whoever was signed in
 * before.
 */
export function useStartSession(): () => Promise<void> {
  const invalidate = useInvalidateContract();
  return () => invalidate();
}

// Seeing and ending sessions (S0.03), and changing a password (S0.06), from
// My account.

/** Every active session of the caller, newest first, flagging the one asking. */
export function useSessions(): QueryResult<SessionsList> {
  return useContractQuery(authTrpcContract, "listSessions", { input: {} });
}

/** Ends one of the caller's own sessions by its listed id. */
export function useEndSession(): MutationResult<{ ok: true }, EndSessionInput> {
  const mutation = useContractMutation(authTrpcContract, "endSession");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<{ ok: true }, EndSessionInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

/** Ends every session of the caller, including this one; the response clears its own cookie too. */
export function useSignOutEverywhere(): () => Promise<void> {
  const mutation = useContractMutation(authTrpcContract, "signOutEverywhere");
  return async () => {
    await mutation.mutateAsync({});
    openSignInFresh();
  };
}

/** Sets a new password after checking the current one; ends every other session (ADR-004 §3). */
export function useChangePassword(): MutationResult<{ ok: true }, ChangePasswordInput> {
  const mutation = useContractMutation(authTrpcContract, "changePassword");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<{ ok: true }, ChangePasswordInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

// Changing the sign-in email (S6.02): the current password proves it, a
// mailed link to the new address confirms it.

/** Checks the current password and mails a confirmation link to the new address, plus a
 * notice to the old one; nothing changes until that link is confirmed. */
export function useRequestEmailChange(): MutationResult<{ ok: true }, RequestEmailChangeInput> {
  return useContractMutation(authTrpcContract, "requestEmailChange") as unknown as MutationResult<
    { ok: true },
    RequestEmailChangeInput
  >;
}

/** Spends a mailed confirmation link's token once, swapping the sign-in address. */
export function useConfirmEmailChange(): MutationResult<EmailChanged, ConfirmEmailChangeInput> {
  const mutation = useContractMutation(authTrpcContract, "confirmEmailChange");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<EmailChanged, ConfirmEmailChangeInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

/** Starts the account's deletion grace period; refused for the last admin of an
 * organization with other members (S6.08). Ends every session, the response clears
 * its own cookie too, the same way signing out everywhere does. */
export function useDeleteAccount(): MutationResult<DeletionStarted, DeleteAccountInput> {
  const mutation = useContractMutation(authTrpcContract, "deleteAccount");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<DeletionStarted, DeleteAccountInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate()),
  };
}

// Passkeys (ADR-004 point 4): adding one from My account, and signing in
// with one from the sign-in screen, with no password.

/** This person's passkeys, newest first. */
export function useListPasskeys(): QueryResult<Passkeys> {
  return useContractQuery(authTrpcContract, "listPasskeys", { input: {} });
}

/** Begins adding a passkey: a registration ceremony's options for `navigator.credentials.create`. */
export function useRequestPasskeyRegistration(): MutationResult<RegistrationOptions, Record<string, never>> {
  return useContractMutation(authTrpcContract, "passkeyRegistrationOptions") as unknown as MutationResult<
    RegistrationOptions,
    Record<string, never>
  >;
}

/** Verifies the registration ceremony's response and saves the new passkey. */
export function useAddPasskey(): MutationResult<Passkey, { response: RegistrationResponseInput }> {
  const mutation = useContractMutation(authTrpcContract, "addPasskey");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<Passkey, { response: RegistrationResponseInput }>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

export function useRenamePasskey(): MutationResult<Passkey, RenamePasskeyInput> {
  const mutation = useContractMutation(authTrpcContract, "renamePasskey");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<Passkey, RenamePasskeyInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

export function useRemovePasskey(): MutationResult<{ ok: true }, { id: string }> {
  const mutation = useContractMutation(authTrpcContract, "removePasskey");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<{ ok: true }, { id: string }>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(authTrpcContract)),
  };
}

/** Begins signing in with a passkey: an authentication ceremony's options for `navigator.credentials.get`. */
export function useRequestPasskeySignIn(): MutationResult<AuthenticationOptions, Record<string, never>> {
  return useContractMutation(authTrpcContract, "passkeySignInOptions") as unknown as MutationResult<
    AuthenticationOptions,
    Record<string, never>
  >;
}

/** Verifies the authentication ceremony's response and signs in. */
export const usePasskeySignIn = (): MutationResult<SignedIn, PasskeySignInInput> => useEnter("passkeySignIn");

export type { AuthenticationResponseInput };

// Google and LinkedIn (ADR-007). Starting answers the provider's address; the caller sends the
// browser there.

/** Begins signing in or up with a provider. */
export function useOidcSignInStart(): MutationResult<OidcRedirect, OidcSignInStartInput> {
  const mutation = useContractMutation(authTrpcContract, "oidcSignInStart");
  return { ...mutation, run: (input) => mutation.mutateAsync(input) };
}

/** Begins connecting a provider to the signed-in account. */
export function useOidcLinkStart(): MutationResult<OidcRedirect, OidcProviderInput> {
  const mutation = useContractMutation(authTrpcContract, "oidcLinkStart");
  return { ...mutation, run: (input) => mutation.mutateAsync(input) };
}

/** Finishes a return from a provider: a sign-in starts a session, a link changes the profile,
 * so every cached read is refreshed either way. */
export function useOidcSignIn(): MutationResult<OidcSignedIn, OidcSignInInput> {
  const mutation = useContractMutation(authTrpcContract, "oidcSignIn");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<OidcSignedIn, OidcSignInInput>;
  return {
    ...typed,
    run: async (input) => {
      const done = await typed.mutateAsync(input);
      await invalidate();
      return done;
    },
  };
}

/** Disconnects a provider from the signed-in account. */
export function useOidcUnlink(): MutationResult<{ ok: true }, OidcProviderInput> {
  const mutation = useContractMutation(authTrpcContract, "oidcUnlink");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<{ ok: true }, OidcProviderInput>;
  return { ...typed, run: (input) => typed.mutateAsync(input).finally(() => void invalidate()) };
}
